import React, { useState, useRef, useCallback, useEffect } from 'react';
import Modal from '../common/Modal';
import { requestPresignedUploadUrl, uploadFileToS3, uploadFileMultipart, completeMultipartUpload, completeUpload } from '../../api/uploadApi';
import { fetchMediaLibrary } from '../../api/mediaApi';

const ALLOWED_EXTENSIONS = new Set([
  // Video
  'mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v', 'mpg', 'mpeg', 'ts', 'mts',
  // Image
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'svg', 'heic', 'heif', 'avif',
  // Audio
  'mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma',
  // Document
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv',
  // Compressed
  'zip', 'rar', '7z', 'tar', 'gz',
]);

function getExtension(fileName) {
  const dot = fileName.lastIndexOf('.');
  if (dot === -1) return '';
  return fileName.slice(dot + 1).toLowerCase();
}

function validateFiles(fileList) {
  const invalid = [];
  for (const file of fileList) {
    const ext = getExtension(file.name);
    if (!ext || !ALLOWED_EXTENSIONS.has(ext)) {
      invalid.push(file.name);
    }
  }
  return invalid;
}

export default function UploadModal({ isOpen, onClose, currentPath, onComplete }) {
  const [files, setFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [allFolders, setAllFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Recursively fetch all folders (root + one level of subfolders)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadAllFolders = async () => {
      setLoadingFolders(true);
      try {
        // 1. Get root folders
        const rootRes = await fetchMediaLibrary('');
        const rootFolders = rootRes.data.folders || [];
        const tree = [];

        // 2. For each root folder, fetch subfolders
        for (const rf of rootFolders) {
          const node = { key: rf.key, name: rf.name, children: [] };
          try {
            const subRes = await fetchMediaLibrary(rf.key);
            const subFolders = subRes.data.folders || [];
            node.children = subFolders.map((sf) => ({
              key: sf.key,
              name: sf.name,
              children: [],
            }));
          } catch {
            // ignore subfolder errors
          }
          tree.push(node);
        }

        if (!cancelled) {
          setAllFolders(tree);

          // Auto-select: match currentPath, or first subfolder, or first root
          const flatKeys = tree.flatMap((n) => [n.key, ...n.children.map((c) => c.key)]);
          const match = flatKeys.find((k) => k === currentPath || k === currentPath + '/');
          if (match) {
            setSelectedFolder(match);
          } else {
            // Prefer first subfolder
            const firstSub = tree.find((n) => n.children.length > 0);
            if (firstSub) {
              setSelectedFolder(firstSub.children[0].key);
            } else if (tree.length > 0) {
              setSelectedFolder(tree[0].key);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load folders:', err);
      } finally {
        if (!cancelled) setLoadingFolders(false);
      }
    };

    loadAllFolders();
    return () => { cancelled = true; };
  }, [isOpen, currentPath]);

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList);
    const invalid = validateFiles(arr);
    if (invalid.length > 0) {
      setError(
        `Unsupported file type${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}. ` +
        'Allowed: videos, images, audio, documents, and archives.'
      );
      // Still add the valid ones
      const valid = arr.filter((f) => {
        const ext = getExtension(f.name);
        return ext && ALLOWED_EXTENSIONS.has(ext);
      });
      setFiles(valid);
    } else {
      setFiles(arr);
      setError('');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }
    if (!selectedFolder) {
      setError('Please select a folder');
      return;
    }

    // Double-check extensions before upload
    const invalid = validateFiles(files);
    if (invalid.length > 0) {
      setError(`Unsupported file type${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`);
      return;
    }

    setUploading(true);
    setError('');

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress((prev) => ({ ...prev, [file.name]: 0 }));

        const presignRes = await requestPresignedUploadUrl({
          path: selectedFolder,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          category: 'raw_video',
          fileSize: file.size,
        });

        const resData = presignRes.data;

        if (resData.multipart) {
          // Multipart upload for large files
          const parts = await uploadFileMultipart(
            resData.partUrls,
            resData.chunkSize,
            file,
            (pct) => setProgress((prev) => ({ ...prev, [file.name]: pct })),
          );

          await completeMultipartUpload({
            s3Key: resData.s3Key,
            uploadId: resData.uploadId,
            parts,
          });

          await completeUpload({
            assetId: resData.assetId,
            fileName: file.name,
            s3Key: resData.s3Key,
            category: 'raw_video',
            fileSize: file.size,
            contentType: file.type,
          });
        } else {
          // Single PUT for small files
          const { uploadUrl, assetId, s3Key } = resData;

          await uploadFileToS3(uploadUrl, file, (pct) => {
            setProgress((prev) => ({ ...prev, [file.name]: pct }));
          });

          await completeUpload({
            assetId,
            fileName: file.name,
            s3Key,
            category: 'raw_video',
            fileSize: file.size,
            contentType: file.type,
          });
        }

        setProgress((prev) => ({ ...prev, [file.name]: 100 }));
      }

      if (onComplete) onComplete();
      setFiles([]);
      setProgress({});
      onClose();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload Files" size="lg">
      <div className="mol-upload-modal">
        <div className="mol-form-group">
          <label className="mol-label">Upload to Folder</label>
          {loadingFolders ? (
            <div className="mol-text-muted" style={{ padding: '8px 0' }}>Loading folders...</div>
          ) : allFolders.length === 0 ? (
            <div className="mol-text-muted" style={{ padding: '8px 0' }}>No folders found. Create a folder first.</div>
          ) : (
            <div className="mol-folder-tree">
              {allFolders.map((folder) => (
                <div key={folder.key} className="mol-folder-tree__group">
                  <div
                    className={`mol-folder-tree__item ${selectedFolder === folder.key ? 'mol-folder-tree__item--selected' : ''}`}
                    onClick={() => setSelectedFolder(folder.key)}
                  >
                    <svg className="mol-folder-tree__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    <span className="mol-folder-tree__name">{folder.name}</span>
                  </div>
                  {folder.children.length > 0 && (
                    <div className="mol-folder-tree__children">
                      {folder.children.map((child) => (
                        <div
                          key={child.key}
                          className={`mol-folder-tree__item mol-folder-tree__item--child ${selectedFolder === child.key ? 'mol-folder-tree__item--selected' : ''}`}
                          onClick={() => setSelectedFolder(child.key)}
                        >
                          <svg className="mol-folder-tree__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                          <span className="mol-folder-tree__name">{child.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className={`mol-dropzone ${dragOver ? 'mol-dropzone--active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <p>Drag &amp; drop files here or click to browse</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {files.length > 0 && (
          <div className="mol-upload-files">
            {files.map((file) => (
              <div key={file.name} className="mol-upload-file">
                <span className="mol-upload-file__name">{file.name}</span>
                <span className="mol-upload-file__size">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
                {progress[file.name] !== undefined && (
                  <div className="mol-upload-progress">
                    <div className="mol-upload-progress__bar" style={{ width: `${progress[file.name]}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {error && <p className="mol-form-error">{error}</p>}

        <div className="mol-modal__actions">
          <button className="mol-btn mol-btn--secondary mol-btn--md" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="mol-btn mol-btn--primary mol-btn--md" onClick={handleUpload} disabled={uploading || files.length === 0}>
            {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
